import { makeVar } from '@apollo/client'
import { useCart } from '@emico-hooks/cart'
import { useCartId, useSetCartId } from '@emico-hooks/cart-id'
import { CartItemUpdateInput } from '@emico-hooks/cart-update-cart'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Plural, t, Trans } from '@lingui/macro'
import { useEffect } from 'react'
import Helmet from 'react-helmet'

import { minWidth } from '@emico/styles'

import styles from './CartPage.module.scss'
import { CartPageContent } from './CartPageContent'
import FreeGiftAvailableModal, {
    giftModalEnabledVar,
} from '../../FreeGiftAvailableModal'
import { RestoredQuote } from '../../graphql/schema.generated'
import Container from '../../layout/Container'
import PageWrapper from '../../layout/PageWrapper'
import PageLoader from '../../presentation/PageLoader'
import theme from '../../theme'
import Heading from '../../typography/Heading'
import overridable from '../../utils/overridable'
import CartPageCrosssell from '../CartPageCrosssell'
import EmptyState from '../CartPageEmptyState/EmptyState'

const StyledCrosssell = styled(CartPageCrosssell)`
    border-top: 1px solid ${theme.colors.graylightest};
    padding-top: 70px;
    padding-bottom: 70px;
    margin: 0 auto;
    max-width: 1100px;
    @media ${minWidth('lg')} {
        padding-top: 80px;
        padding-bottom: 80px;
    }
`

const headerStyles = css`
    display: flex;
    align-items: center;
    gap: 0.5em;
`

export const StyledHeading = styled(Heading)`
    ${headerStyles}
    margin-bottom: 10px;
`

const StyledItemCount = styled.span`
    font-size: 0.5em;
    text-transform: lowercase;
    font-weight: normal;
`

export const ItemCount = ({ quantity }: { quantity: number }) => (
    <StyledItemCount as="span">
        (
        <Plural
            id="cart.cartItemListing.numberOfItems"
            value={quantity}
            one="# item"
            zero="# items"
            other="# items"
        />
        )
    </StyledItemCount>
)

export const restoredQuoteVar = makeVar<RestoredQuote | undefined | null>(
    undefined,
)

enum OperationKind {
    UPDATE = 'updateCartItems',
    REMOVE = 'removeItemFromCart',
}

type OperationInput = {
    cartId: string
    cartItemUid?: string
    cartItems?: Array<
        Pick<
            CartItemUpdateInput,
            'cart_item_uid' | 'configurable_options' | 'quantity'
        >
    >
}

const CartPage = () => {
    const cartId = useCartId()
    const setCartId = useSetCartId()
    const { data: cart, loading } = useCart()

    if (cartId) {
        setCartId(cartId)
    }

    useEffect(() => {
        giftModalEnabledVar(true)
    }, [])

    let content

    if (cart?.items?.length === 0 || !cartId) {
        content = <EmptyState />
    } else if (loading) {
        content = (
            <PageLoader
                fullScreen
                reason={
                    <Trans id="cart.pageLoader.reason">
                        Loading cart items
                    </Trans>
                }
            />
        )
    } else {
        content = (
            <>
                <Container>
                    <CartPageContent />
                </Container>
                <Container>
                    <StyledCrosssell />
                </Container>
                <FreeGiftAvailableModal />
            </>
        )
    }

    return (
        <>
            <Helmet>
                <title>
                    {t({
                        id: 'cart.cartPage.pageTitle',
                        message: `Your cart`,
                    })}
                </title>
            </Helmet>
            <PageWrapper pageType="cart" contentClassName={styles.wrapper}>
                {content}
            </PageWrapper>
        </>
    )
}

export default overridable('CartPage')(CartPage)
